import { useMutation, useQueries, useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosError, AxiosResponse, AxiosInstance } from 'axios';
import { QueryKeys } from '../enums/QueryKeys';
import {
  TimeSeriesData,
  DevicesReadingsAvgQueryParams,
  DevicesReadingsQueryParams,
  QueryOptions,
  DeviceLastReadingDto,
  TimeSeriesReading,
  ReadingsRequestDto,
  DeviceReadingsResponseDto,
  MutationOptions,
  MutationResult,
  ErrorResponse,
} from '@thingslog/repositories';

export function createDeviceCountersQueryClient(axios: AxiosInstance): DeviceCountersQueryClient {
  return new DeviceCountersQueryClientImp(axios);
}

class DeviceCountersQueryClientImp {
  public constructor(private axios: AxiosInstance) {}

  public getCurrentReadings = async (
    deviceNumber: string,
    every: number
  ): Promise<DeviceLastReadingDto[]> => {
    return this.axios
      .get(`/api/v2/devices/${deviceNumber}/readings/current`, {
        params: { every: every },
      })
      .then((response: AxiosResponse) => {
        return response.data;
      });
  };

  public useCurrentReadings = (
    deviceNumber: string,
    every: number,
    options?: QueryOptions<DeviceLastReadingDto[]>
  ): UseQueryResult<DeviceLastReadingDto[], AxiosError> => {
    return useQuery<DeviceLastReadingDto[], AxiosError>(
      [QueryKeys.GetCurrentReadings, deviceNumber, every],
      () => this.getCurrentReadings(deviceNumber, every),
      options
    );
  };

  public useDevicesReadingsQueries = (
    args: DevicesReadingsQueryParams<TimeSeriesData<number>[]>[]
  ): UseQueryResult<TimeSeriesData<number>[], AxiosError>[] =>
    useQueries({
      queries: args.map(
        ({
          deviceNumber,
          sensorIndex,
          fromDate,
          toDate,
          every,
          options,
        }: DevicesReadingsQueryParams<TimeSeriesData<number>[]>) => ({
          queryKey: [
            QueryKeys.GetDeviceSensorReadings,
            deviceNumber,
            sensorIndex,
            fromDate,
            toDate,
            every,
          ],
          queryFn: () =>
            this.getDeviceSensorReadingsTimeSeriesData(
              deviceNumber,
              sensorIndex,
              fromDate,
              toDate,
              every
            ),
          ...options,
        })
      ),
    });

  public useDevicesReadingsAvgQueries = (
    args: DevicesReadingsAvgQueryParams<TimeSeriesData<number>[]>[]
  ): UseQueryResult<TimeSeriesData<number>[], AxiosError>[] =>
    useQueries({
      queries: args.map(
        ({
          deviceNumber,
          sensorIndex,
          avgFromDate,
          avgEndDate,
          fromDate,
          toDate,
          every,
          options,
        }: DevicesReadingsAvgQueryParams<TimeSeriesData<number>[]>) => ({
          queryKey: [
            QueryKeys.GetDevicesReadingsAvg,
            deviceNumber,
            sensorIndex,
            avgFromDate,
            avgEndDate,
            fromDate,
            toDate,
            every,
          ],
          queryFn: () =>
            this.getDeviceReadingsAvgTimeSeriesData(
              deviceNumber,
              sensorIndex,
              avgFromDate,
              avgEndDate,
              fromDate,
              toDate,
              every
            ),
          ...options,
        })
      ),
    });

  public useDeviceSensorReadings = (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number,
    options?: QueryOptions<TimeSeriesData<number>[]>
  ): UseQueryResult<TimeSeriesData<number>[], AxiosError> => {
    return useQuery<TimeSeriesData<number>[], AxiosError>(
      [QueryKeys.GetDeviceSensorReadings, deviceNumber, sensorIndex, fromDate, toDate, every],
      () =>
        this.getDeviceSensorReadingsTimeSeriesData(
          deviceNumber,
          sensorIndex,
          fromDate,
          toDate,
          every
        ),
      options
    );
  };

  public getDeviceSensorReadingsTimeSeriesData = async (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number
  ): Promise<TimeSeriesData<number>[]> => {
    return await this.axios
      .get(`/api/v2/devices/${deviceNumber}/${sensorIndex}/readings`, {
        params: {
          fromDate: fromDate,
          toDate: toDate,
          every: every,
        },
      })
      .then((response: AxiosResponse) => {
        const values = response.data;
        const timeSeriesData: TimeSeriesData<number>[] = [];
        values.forEach(({ date, reading }: { date: Date; reading: number }) => {
          const timestamp = new Date(date).getTime();
          const readingDataPoint: TimeSeriesData<number> = { timestamp: timestamp, value: reading };
          timeSeriesData.push(readingDataPoint);
        });

        return timeSeriesData;
      });
  };

  public useDeviceReadings = (
    deviceNumber: string,
    fromDate: string,
    toDate: string,
    every: number,
    options?: QueryOptions<Record<string, TimeSeriesReading<number>[]>>
  ): UseQueryResult<Record<string, TimeSeriesReading<number>[]>, AxiosError> => {
    return useQuery<Record<string, TimeSeriesReading<number>[]>, AxiosError>(
      [QueryKeys.GetDeviceReadings, deviceNumber, fromDate, toDate, every],
      () => this.getDeviceReadingsTimeSeriesData(deviceNumber, fromDate, toDate, every),
      options
    );
  };

  public getDeviceReadingsTimeSeriesData = async (
    deviceNumber: string,
    fromDate: string,
    toDate: string,
    every: number
  ): Promise<Record<string, TimeSeriesReading<number>[]>> => {
    return await this.axios
      .get(`/api/v2/devices/${deviceNumber}/readings`, {
        params: {
          fromDate: fromDate,
          toDate: toDate,
          every: every,
        },
      })
      .then((response: AxiosResponse) => {
        return response.data;
      });
  };

  public getDeviceReadingsAvgTimeSeriesData = async (
    deviceNumber: string,
    sensorIndex: number,
    avgFromDate: Date,
    avgEndDate: Date,
    fromDate: Date,
    toDate: Date,
    every: number
  ): Promise<TimeSeriesData<number>[]> => {
    const targetStartDate = new Date(fromDate).toISOString().substring(0, 10);
    const targetEndDate = new Date(toDate).toISOString().substring(0, 10);
    return await this.axios
      .get(`/api/v2/devices/${deviceNumber}/${sensorIndex}/readings/avg`, {
        params: {
          fromDate: avgFromDate,
          toDate: avgEndDate,
          targetFromDate: targetStartDate,
          targetToDate: targetEndDate,
          every: every,
        },
      })
      .then((response: AxiosResponse) => {
        const values = response.data.value;
        const timeSeriesData: TimeSeriesData<number>[] = [];
        values.forEach(({ date, reading }: { date: Date; reading: number }) => {
          const timestamp = new Date(date).getTime();
          const readingDataPoint: TimeSeriesData<number> = { timestamp: timestamp, value: reading };
          timeSeriesData.push(readingDataPoint);
        });

        return timeSeriesData;
      });
  };

  public fetchDevicesReadings = async (
    readingsRequestDto: ReadingsRequestDto,
    fromDate: string,
    toDate: string
  ): Promise<DeviceReadingsResponseDto> => {
    return await this.axios
      .post('/api/devices/fetch-readings', readingsRequestDto, {
        params: {
          fromDate: fromDate,
          toDate: toDate,
        },
      })
      .then((response: AxiosResponse) => {
        return response.data;
      });
  };

  public useFetchDevicesReadings = (
    readingsRequestDto: ReadingsRequestDto,
    fromDate: string,
    toDate: string,
    options?: QueryOptions<DeviceReadingsResponseDto>
  ): UseQueryResult<DeviceReadingsResponseDto, AxiosError> => {
    return useQuery<DeviceReadingsResponseDto, AxiosError>(
      [QueryKeys.FetchDevicesReadings, readingsRequestDto, fromDate, toDate],
      () => this.fetchDevicesReadings(readingsRequestDto, fromDate, toDate),
      options
    );
  };

  public useFetchDevicesReadingsMutation = (
    fromDate: string,
    toDate: string,
    options?: MutationOptions<DeviceReadingsResponseDto, ReadingsRequestDto>
  ): MutationResult<DeviceReadingsResponseDto, ReadingsRequestDto> =>
    useMutation<DeviceReadingsResponseDto, AxiosError<ErrorResponse>, ReadingsRequestDto>(
      (variables: ReadingsRequestDto) => this.fetchDevicesReadings(variables, fromDate, toDate),
      options
    );
}

export interface DeviceCountersQueryClient {
  useDevicesReadingsQueries: (
    args: DevicesReadingsQueryParams<TimeSeriesData<number>[]>[]
  ) => UseQueryResult<TimeSeriesData<number>[], AxiosError>[];

  useDevicesReadingsAvgQueries: (
    args: DevicesReadingsAvgQueryParams<TimeSeriesData<number>[]>[]
  ) => UseQueryResult<TimeSeriesData<number>[], AxiosError>[];

  getDeviceSensorReadingsTimeSeriesData: (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number
  ) => Promise<TimeSeriesData<number>[]>;

  getDeviceReadingsAvgTimeSeriesData: (
    deviceNumber: string,
    sensorIndex: number,
    avgFromDate: Date,
    avgEndDate: Date,
    fromDate: Date,
    toDate: Date,
    every: number
  ) => Promise<TimeSeriesData<number>[]>;

  useCurrentReadings: (
    deviceNumber: string,
    every: number,
    options?: QueryOptions<DeviceLastReadingDto[]>
  ) => UseQueryResult<DeviceLastReadingDto[], AxiosError>;

  getCurrentReadings: (deviceNumber: string, every: number) => Promise<DeviceLastReadingDto[]>;

  useDeviceSensorReadings: (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number,
    options?: QueryOptions<TimeSeriesData<number>[]>
  ) => UseQueryResult<TimeSeriesData<number>[], AxiosError>;

  useDeviceReadings: (
    deviceNumber: string,
    fromDate: string,
    toDate: string,
    every: number,
    options?: QueryOptions<Record<string, TimeSeriesReading<number>[]>>
  ) => UseQueryResult<Record<string, TimeSeriesReading<number>[]>, AxiosError>;

  getDeviceReadingsTimeSeriesData: (
    deviceNumber: string,
    fromDate: string,
    toDate: string,
    every: number
  ) => Promise<Record<string, TimeSeriesReading<number>[]>>;

  fetchDevicesReadings: (
    readingsRequestDto: ReadingsRequestDto,
    fromDate: string,
    toDate: string
  ) => Promise<DeviceReadingsResponseDto>;

  useFetchDevicesReadings: (
    readingsRequestDto: ReadingsRequestDto,
    fromDate: string,
    toDate: string,
    options?: QueryOptions<DeviceReadingsResponseDto>
  ) => UseQueryResult<DeviceReadingsResponseDto, AxiosError>;

  useFetchDevicesReadingsMutation: (
    fromDate: string,
    toDate: string,
    options?: MutationOptions<DeviceReadingsResponseDto, ReadingsRequestDto>
  ) => MutationResult<DeviceReadingsResponseDto, ReadingsRequestDto>;
}
